import { fromServerPreferencePayload, type UserPreferences } from '@finance/contracts';
import i18n from '../../i18n';
import { getLanguageTag } from '../../i18n/locale-registry';

/** Apply user preferences that have concrete browser/runtime behavior. */
export function applyPreferencesRuntime(preferences: UserPreferences): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  void i18n.changeLanguage(preferences.language);
  document.documentElement.lang = getLanguageTag(preferences.language);
  document.documentElement.dataset.theme = preferences.theme;
  document.documentElement.dataset.dateFormat = preferences.dateFormat;
  document.documentElement.dataset.numberFormat = preferences.numberFormat;
  document.documentElement.dataset.timeZone = preferences.timeZone ?? '';

  const values: Array<[string, string]> = [
    ['finance:theme', preferences.theme],
    ['finance:language', preferences.language],
    ['finance:date-format', preferences.dateFormat],
    ['finance:number-format', preferences.numberFormat],
    ['finance:time-zone', preferences.timeZone ?? ''],
    ['finance:default-page-size', String(preferences.defaultPageSize)],
  ];
  try {
    for (const [key, value] of values) window.localStorage?.setItem(key, value);
  } catch {
    // Storage quota or access errors are non-fatal; document state and formatter events still apply.
  }
  try {
    window.dispatchEvent(new CustomEvent('finance:preferences:changed', { detail: preferences }));
  } catch {
    // Constrained browsers may not support CustomEvent.
  }
}

/** Revert runtime DOM state to a previous known-good preference set after a failed save. */
export function revertPreferencesRuntime(previous: UserPreferences): void {
  applyPreferencesRuntime(previous);
}

/** Apply the same canonical server preference envelope used during session hydration. */
export function applyServerPreferencesRuntime(payload: unknown): void {
  applyPreferencesRuntime(fromServerPreferencePayload(payload));
}
